import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FrogFace } from "@/components/FrogArt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, KeyRound, Droplet } from "lucide-react";

export default function Landing() {
  const nav = useNavigate();
  const [mode, setMode] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("frogfirst_code")) nav("/home");
  }, [nav]);

  const create = async () => {
    setBusy(true);
    try {
      const hh = await api.createHousehold(name || "My Household");
      localStorage.setItem("frogfirst_code", hh.code);
      toast.success(`Household created! Your code is ${hh.code}`);
      nav("/home");
    } catch {
      toast.error("Could not create household. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const join = async () => {
    setBusy(true);
    try {
      await api.getHousehold(code.trim().toUpperCase());
      localStorage.setItem("frogfirst_code", code.trim().toUpperCase());
      nav("/home");
    } catch {
      toast.error("Code not found. Double-check and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F9FF] relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-sky-200/60" />
      <div className="absolute top-40 -right-20 w-64 h-64 rounded-full bg-cyan-200/40" />
      <div className="absolute -bottom-32 -left-24 w-[500px] h-[500px] rounded-full bg-lime-200/60" />
      <div className="relative max-w-2xl mx-auto px-6 pt-16 pb-24 sm:pt-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-start gap-6">
          <motion.div animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}>
            <FrogFace size={110} />
          </motion.div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 tracking-tight">
            <span className="bg-gradient-to-br from-sky-500 via-cyan-500 to-blue-500 bg-clip-text text-transparent">Aqua</span> <span className="text-green-500">Tadpole</span>
          </h1>
          <p className="text-base md:text-lg text-slate-600 max-w-md">
            A 14-day family program where kids catch the <span className="font-bold text-sky-600">hardest homework first</span> — and parents stop nagging. One frog a day. That's it.
          </p>

          {!mode && (
            <div className="flex flex-col sm:flex-row gap-4 mt-4 w-full">
              <Button
                data-testid="create-household-btn"
                onClick={() => setMode("create")}
                className="btn-tactile h-16 px-8 rounded-2xl bg-gradient-to-br from-sky-500 via-cyan-500 to-blue-500 hover:brightness-110 text-white font-display font-semibold text-lg flex-1"
              >
                <Droplet className="w-5 h-5 mr-2" /> Start our 14 days
              </Button>
              <Button
                data-testid="join-household-btn"
                onClick={() => setMode("join")}
                variant="outline"
                className="btn-tactile h-16 px-8 rounded-2xl bg-white font-display font-semibold text-lg flex-1 border-2 border-slate-200"
              >
                <KeyRound className="w-5 h-5 mr-2" /> I have a code
              </Button>
            </div>
          )}

          {mode === "create" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full bg-white rounded-3xl p-8 shadow-lg border border-slate-100 space-y-4">
              <p className="font-display font-semibold text-slate-800 text-lg">Name your household</p>
              <Input
                data-testid="household-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="The Martins"
                className="h-14 rounded-xl text-lg"
              />
              <p className="text-sm text-slate-500">No email, no password. You'll get a simple 6-letter code to come back anytime.</p>
              <div className="flex gap-3">
                <Button data-testid="confirm-create-btn" onClick={create} disabled={busy} className="btn-tactile h-14 rounded-xl bg-green-500 hover:bg-green-600 font-display font-semibold flex-1">
                  Create household <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="ghost" onClick={() => setMode(null)} className="h-14 rounded-xl">Back</Button>
              </div>
            </motion.div>
          )}

          {mode === "join" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full bg-white rounded-3xl p-8 shadow-lg border border-slate-100 space-y-4">
              <p className="font-display font-semibold text-slate-800 text-lg">Enter your household code</p>
              <Input
                data-testid="household-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                maxLength={6}
                className="h-14 rounded-xl text-2xl font-display tracking-[0.4em] text-center"
              />
              <div className="flex gap-3">
                <Button data-testid="confirm-join-btn" onClick={join} disabled={busy || code.length < 6} className="btn-tactile h-14 rounded-xl bg-sky-500 hover:bg-sky-600 font-display font-semibold flex-1">
                  Jump back in <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="ghost" onClick={() => setMode(null)} className="h-14 rounded-xl">Back</Button>
              </div>
            </motion.div>
          )}

          <div className="grid sm:grid-cols-3 gap-4 mt-10 w-full">
            {[
              ["💧 One frog a day", "The hardest task gets caught first. No overwhelm."],
              ["4 age bands", "K–2 catches frogs with pictures; teens run the full ABCDE method."],
              ["Printable pack", "Frog cards, wall chart & parent scripts for screen-free days."],
            ].map(([t, d]) => (
              <div key={t} className="bg-white/80 backdrop-blur rounded-2xl p-5 border border-slate-100">
                <p className="font-display font-semibold text-slate-800">{t}</p>
                <p className="text-sm text-slate-500 mt-1">{d}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
